import { useState } from 'react';
import { Button } from '../ui/Button';
import { trpc } from '../../lib/trpc';
import { FormSection } from '../ui/FormSection';

interface PhoneSectionProps {
  currentPhone: string | null | undefined;
  phoneVerified: boolean;
}

export const PhoneSection = ({ currentPhone, phoneVerified }: PhoneSectionProps) => {
  const [phoneNumber, setPhoneNumber] = useState(currentPhone || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const sendCode = trpc.auth.sendPhoneCode.useMutation({
    onSuccess: () => {
      setCodeSent(true);
      setSuccessMessage(null);
    },
  });

  const verifyCode = trpc.auth.verifyPhoneCode.useMutation({
    onSuccess: () => {
      setCodeSent(false);
      setVerificationCode('');
      setSuccessMessage('Phone number verified successfully.');
      utils.user.getMe.invalidate();
      utils.auth.getLinkedProviders.invalidate();
    },
  });

  const handleSendCode = () => {
    setSuccessMessage(null);
    sendCode.mutate({ phoneNumber });
  };

  const handleVerifyCode = () => {
    verifyCode.mutate({ phoneNumber, code: verificationCode });
  };

  return (
    <FormSection title="Phone Number">
      {currentPhone && (
        <div className="mb-4 flex items-center gap-2">
          <p className="text-sm text-gray-700">{currentPhone}</p>
          {phoneVerified ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Verified
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Not verified
            </span>
          )}
        </div>
      )}

      {successMessage && <p className="text-sm text-green-600 mb-3">{successMessage}</p>}

      {!codeSent ? (
        <div className="max-w-md">
          <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-1">
            {currentPhone ? 'Update Phone Number' : 'Add Phone Number'}
          </label>
          <div className="flex gap-3">
            <input
              id="phone-number"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1 (555) 123-4567"
              disabled={sendCode.isPending}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSendCode}
              disabled={phoneNumber.length < 10 || sendCode.isPending}
              loading={sendCode.isPending}
            >
              Send Code
            </Button>
          </div>
          {sendCode.error && (
            <p className="text-sm text-red-600 mt-2">{sendCode.error.message}</p>
          )}
        </div>
      ) : (
        <div className="max-w-md">
          <p className="text-sm text-gray-600 mb-3">
            A verification code has been sent to <strong>{phoneNumber}</strong>.
          </p>
          <label
            htmlFor="verification-code"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Verification Code
          </label>
          <div className="flex gap-3">
            <input
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              disabled={verifyCode.isPending}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 disabled:opacity-50"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleVerifyCode}
              disabled={verificationCode.length < 4 || verifyCode.isPending}
              loading={verifyCode.isPending}
            >
              Verify
            </Button>
          </div>
          {verifyCode.error && (
            <p className="text-sm text-red-600 mt-2">{verifyCode.error.message}</p>
          )}
          <button
            onClick={() => {
              setCodeSent(false);
              setVerificationCode('');
            }}
            className="text-sm text-primary-600 hover:text-primary-800 mt-2"
          >
            Use a different number
          </button>
        </div>
      )}
    </FormSection>
  );
};
